/**
 * Live streaming plumbing for the Infrastructure (Docker control plane) area.
 *
 * The InstanceDetail view needs THREE live, staff-only streams off a single
 * container: logs, stats, and an interactive exec session. Unlike the DevOps
 * feed (one app-level `devops-event`), these are bidirectional RAW Socket.io
 * events — the client emits `*:subscribe` / `*:input` and receives `*:data` /
 * `*:output`. This module owns the socket lifecycle so the view components stay
 * thin and leak-free.
 *
 * SECURITY (mirrors the task's hard rules):
 *  - These streams are STAFF-ONLY. They ride the same JWT-authenticated socket
 *    the rest of the app uses; the API authorises every subscribe and NEVER
 *    publishes instance streams to portal `client/<id>` channels.
 *  - Every stream is torn down on unsubscribe AND on socket `disconnect`
 *    (see `wireSocketTeardown`) so a dropped connection never leaves a
 *    half-open stream. On reconnect the view re-subscribes from scratch.
 *  - We never send a raw container id — only the `instanceId` (client_instances
 *    PK). The API validates it against the persisted map and resolves the
 *    container + host key server-side.
 *  - Exec commands are sent as an argv ARRAY, never a shell string.
 *  - No secret (Portainer key) is ever sent or received here.
 *
 * SOURCE OF THE RAW SOCKET: the shared realtime bus (`src/realtime/`). These
 * streams are request/response conversations rather than a fan-out feed, so
 * instead of registering a translated channel this module LEASES the bus's one
 * connection and drives it directly. Every lease is released when its stream
 * stops, so the streams participate in the same single refcount as every other
 * realtime consumer — they cannot keep the connection alive after teardown, and
 * they cannot open a second one.
 *
 * This module used to reach into `rootStore.realtimeStore.socket` and call
 * `realtimeStore.connect()`. That coupled the infrastructure console to one
 * particular store's private socket; the bus is now the only connection owner
 * and RealtimeStore is just another one of its consumers.
 *
 * AUTH: sourced internally by the bus from the AuthStore. If no socket can be
 * established the bus records a loud diagnostic and these functions return
 * null/no-op handles, which the InstanceDetail store surfaces as "socket
 * disconnected" — never a fake 'streaming' state.
 *
 * DOCUMENTED FALLBACK / UNTESTABLE: the live Docker side of this module is
 * runtime-untestable in this environment (no Portainer/Docker, no API Phase 5).
 * The event names and payload shapes are the agreed contract (see
 * infrastructure.types.ts). If the API has not wired a given event, the
 * corresponding stream simply receives nothing and the panel shows its
 * idle/empty state.
 */
import type { Socket } from "socket.io-client";
import type RootStore from "stores/RootStore";
import {
  acquireRealtimeSocket,
  instanceActionProgressChannel,
  instanceStatusChannel,
  instanceUpdateChannel,
  isRealtimeConnected,
  peekRealtimeSocket,
  subscribeRealtime,
  type RealtimeLease,
} from "realtime";
import {
  INSTANCE_LOGS_SUBSCRIBE,
  INSTANCE_LOGS_UNSUBSCRIBE,
  INSTANCE_LOGS_DATA,
  INSTANCE_STATS_SUBSCRIBE,
  INSTANCE_STATS_UNSUBSCRIBE,
  INSTANCE_STATS_DATA,
  INSTANCE_EXEC_START,
  INSTANCE_EXEC_INPUT,
  INSTANCE_EXEC_RESIZE,
  INSTANCE_EXEC_OUTPUT,
  INSTANCE_EXEC_READY,
  INSTANCE_EXEC_STOP,
  INSTANCE_EXEC_END,
  INSTANCE_ERROR,
  type InstanceLogData,
  type InstanceStatsData,
  type InstanceExecOutput,
  type InstanceExecStartPayload,
  type InstanceStreamError,
  type InstanceStatusEnvelope,
  type InstanceActionProgressEvent,
  type InstanceUpdateEnvelope,
} from "./infrastructure.types";

/** Area name these streams are refcounted + diagnosed under on the bus. */
const STREAM_AREA = "instance-streams";

/**
 * Take a lease on the shared connection and hand back the raw socket.
 * Returns `{ socket: null }` when no connection could be established — the
 * caller surfaces "not connected" and may retry. The lease is returned so the
 * caller can release it when its stream stops.
 */
async function leaseSocket(
  rootStore: RootStore
): Promise<{ socket: Socket | null; release: () => void }> {
  const lease: RealtimeLease = await acquireRealtimeSocket(
    rootStore,
    STREAM_AREA
  );
  const socket = lease.socket?.connected ? lease.socket : null;
  if (!socket) {
    // Nothing to drive — don't hold the connection open for a dead stream.
    lease.release();
    return { socket: null, release: () => {} };
  }
  return { socket, release: () => lease.release() };
}

/**
 * Resolve the shared raw socket, taking (and immediately releasing) a lease.
 * Returns null if no socket is available *right now* — callers treat that as
 * "not connected" and may retry (the InstanceDetail store retries on a timer).
 *
 * Prefer the lease-holding stream helpers below; this exists for callers that
 * only need to look.
 */
export async function ensureSocket(
  rootStore: RootStore
): Promise<Socket | null> {
  const { socket, release } = await leaseSocket(rootStore);
  release();
  return socket;
}

/** True when the shared socket is currently connected. */
export function isSocketConnected(rootStore: RootStore): boolean {
  return isRealtimeConnected(rootStore);
}

/**
 * Observe the shared socket's connect/disconnect transitions so a view can keep
 * its "socket connected" indicator honest AND re-establish its streams after an
 * auto-reconnect (socket.io reuses the same Socket object but the server-side
 * subscriptions are gone, and the per-stream `wireSocketTeardown` already tore
 * the client handles down on the `disconnect`). Returns a disposer.
 *
 * Leak-safe by construction: this only NOTIFIES the caller; it never subscribes
 * to instance streams itself. The caller's `start*` are guarded against
 * double-subscribe (handle-null checks) and its `dispose()` is idempotent, so
 * re-subscribing on `connect` cannot stack duplicate streams.
 *
 * Best-effort: when no socket OBJECT exists at all this returns null (NOT a
 * no-op disposer) so the caller knows it is unbound and can retry binding the
 * next time it touches the streams.
 */
export async function observeConnection(
  rootStore: RootStore,
  onConnect: () => void,
  onDisconnect: () => void
): Promise<(() => void) | null> {
  const lease = await acquireRealtimeSocket(rootStore, STREAM_AREA);
  // Even if not connected yet, bind to whatever socket OBJECT exists so a later
  // `connect` is caught; report unbound (null) when there's none.
  const sock = lease.socket ?? peekRealtimeSocket(rootStore);
  if (!sock) {
    lease.release();
    return null;
  }
  const handleConnect = () => onConnect();
  const handleDisconnect = () => onDisconnect();
  sock.on("connect", handleConnect);
  sock.on("disconnect", handleDisconnect);
  return () => {
    sock.off("connect", handleConnect);
    sock.off("disconnect", handleDisconnect);
    lease.release();
  };
}

/**
 * Listen for the API's `instance:error` channel (auth denial, already-subscribed,
 * concurrent-stream cap, not-found, transport errors). Returns a disposer that
 * removes the listener. Callers route the error to the right panel by `kind`.
 * Best-effort: if there's no socket yet, this is a no-op (the store retries when
 * the streams (re)subscribe).
 */
export async function subscribeInstanceErrors(
  rootStore: RootStore,
  onError: (err: InstanceStreamError) => void
): Promise<() => void> {
  const { socket, release } = await leaseSocket(rootStore);
  if (!socket) return () => {};
  const handler = (raw: unknown) => {
    const err = (raw ?? {}) as Partial<InstanceStreamError>;
    if (!err.message) return;
    try {
      onError({ kind: err.kind ?? "logs", message: err.message });
    } catch (e) {
      console.error("[instance error] handler error:", e);
    }
  };
  socket.on(INSTANCE_ERROR, handler);
  const detach = wireSocketTeardown(socket, () =>
    socket.off(INSTANCE_ERROR, handler)
  );
  let released = false;
  return () => {
    socket.off(INSTANCE_ERROR, handler);
    detach();
    if (!released) {
      released = true;
      release();
    }
  };
}

/**
 * A single live subscription. Callers hold the returned handle and MUST call
 * `.stop()` on unmount/route-change. `.stop()` is idempotent and also detaches
 * the `disconnect` teardown listener.
 */
export interface StreamHandle {
  stop: () => void;
}

/**
 * Internal: attach a one-shot teardown that fires on socket `disconnect` so a
 * dropped connection tears the stream down even if the component never calls
 * stop(). Returns a detach fn.
 */
function wireSocketTeardown(socket: Socket, teardown: () => void): () => void {
  const onDisconnect = () => teardown();
  socket.on("disconnect", onDisconnect);
  return () => socket.off("disconnect", onDisconnect);
}

/**
 * Subscribe to a container's LOG stream. Emits `instance:logs:subscribe` with
 * the instanceId and routes every `instance:logs:data` for THIS instance to
 * `onData`. Tear-down emits `instance:logs:unsubscribe`, removes the data
 * listener, and detaches the disconnect handler.
 *
 * Returns null when the shared socket is not connected — the caller must
 * surface "socket disconnected" instead of pretending to stream (a no-op
 * handle would fake a 'streaming' state that nothing feeds).
 */
export async function subscribeLogs(
  rootStore: RootStore,
  instanceId: string,
  onData: (data: InstanceLogData) => void
): Promise<StreamHandle | null> {
  const { socket, release } = await leaseSocket(rootStore);
  if (!socket) return null;

  const dataHandler = (raw: unknown) => {
    const data = raw as InstanceLogData;
    // Defensive: only forward events for the instance we asked about. A
    // mis-scoped server (or a shared socket carrying multiple subscriptions)
    // must never bleed another container's logs into this console.
    if (!data || data.instanceId !== instanceId) return;
    try {
      onData(data);
    } catch (err) {
      console.error("[instance logs] handler error:", err);
    }
  };

  let stopped = false;
  let detachTeardown: () => void = () => {};

  const stop = () => {
    if (stopped) return;
    stopped = true;
    socket.off(INSTANCE_LOGS_DATA, dataHandler);
    detachTeardown();
    // Best-effort: tell the API to stop streaming for this instance.
    try {
      socket.emit(INSTANCE_LOGS_UNSUBSCRIBE, { instanceId });
    } catch {
      // ignore — the disconnect path also cleans up server-side.
    }
    release();
  };

  socket.on(INSTANCE_LOGS_DATA, dataHandler);
  detachTeardown = wireSocketTeardown(socket, stop);
  socket.emit(INSTANCE_LOGS_SUBSCRIBE, { instanceId });

  return { stop };
}

/** Subscribe to a container's STATS stream (same lifecycle as logs; null when
 *  the socket is not connected — see {@link subscribeLogs}). */
export async function subscribeStats(
  rootStore: RootStore,
  instanceId: string,
  onData: (data: InstanceStatsData) => void
): Promise<StreamHandle | null> {
  const { socket, release } = await leaseSocket(rootStore);
  if (!socket) return null;

  const dataHandler = (raw: unknown) => {
    const data = raw as InstanceStatsData;
    if (!data || data.instanceId !== instanceId) return;
    try {
      onData(data);
    } catch (err) {
      console.error("[instance stats] handler error:", err);
    }
  };

  let stopped = false;
  let detachTeardown: () => void = () => {};

  const stop = () => {
    if (stopped) return;
    stopped = true;
    socket.off(INSTANCE_STATS_DATA, dataHandler);
    detachTeardown();
    try {
      socket.emit(INSTANCE_STATS_UNSUBSCRIBE, { instanceId });
    } catch {
      // ignore
    }
    release();
  };

  socket.on(INSTANCE_STATS_DATA, dataHandler);
  detachTeardown = wireSocketTeardown(socket, stop);
  socket.emit(INSTANCE_STATS_SUBSCRIBE, { instanceId });

  return { stop };
}

/**
 * An interactive exec session handle (admin only). `send` pushes keystrokes,
 * `resize` reports the xterm geometry so the API can relay it to the Docker
 * PTY (`/exec/{id}/resize` — the execId stays server-side), and `stop` ends
 * the session and removes every listener.
 *
 * The API keys at most ONE exec stream per container by instanceId — there is
 * no sessionId to correlate. All events are scoped on instanceId.
 */
export interface ExecSessionHandle {
  send: (input: string) => void;
  resize: (cols: number, rows: number) => void;
  stop: () => void;
}

/**
 * Start an exec session against a container. The argv ARRAY is sent under `cmd`
 * (never a shell string). Output for this instance is routed to `onOutput`;
 * `onReady` fires when the PTY is open (`instance:exec:ready`); `onEnd` fires
 * when the API signals the process closed (`instance:exec:end`). `stop()` emits
 * `instance:exec:stop` and removes all listeners. On socket disconnect the
 * stream is torn down locally without emitting (the socket is already gone).
 *
 * ADMIN ONLY — the caller gates rendering on the admin tier; the API
 * independently enforces it on `instance:exec:start`.
 */
export async function startExecSession(
  rootStore: RootStore,
  payload: InstanceExecStartPayload,
  onOutput: (chunk: string) => void,
  onEnd?: () => void,
  onReady?: () => void
): Promise<ExecSessionHandle | null> {
  const { socket, release } = await leaseSocket(rootStore);
  // Null when the socket is down — the caller surfaces "socket disconnected"
  // rather than holding a dead handle.
  if (!socket) return null;

  let stopped = false;
  let detachTeardown: () => void = () => {};

  const outputHandler = (raw: unknown) => {
    const out = raw as InstanceExecOutput;
    if (!out || out.instanceId !== payload.instanceId) return;
    try {
      onOutput(out.data ?? "");
    } catch (err) {
      console.error("[instance exec] output handler error:", err);
    }
  };

  const readyHandler = (raw: unknown) => {
    const out = (raw ?? {}) as { instanceId?: string };
    if (out.instanceId && out.instanceId !== payload.instanceId) return;
    onReady?.();
  };

  const endHandler = (raw: unknown) => {
    const out = (raw ?? {}) as { instanceId?: string };
    if (out.instanceId && out.instanceId !== payload.instanceId) return;
    // Mark stopped so a later handle.stop() (e.g. the component's unmount path)
    // is a clean no-op and never re-emits instance:exec:stop for a session the
    // server already ended. stop()/the disconnect teardown both honour `stopped`.
    stopped = true;
    detachListeners();
    onEnd?.();
  };

  let released = false;
  const detachListeners = () => {
    socket.off(INSTANCE_EXEC_OUTPUT, outputHandler);
    socket.off(INSTANCE_EXEC_READY, readyHandler);
    socket.off(INSTANCE_EXEC_END, endHandler);
    detachTeardown();
    if (!released) {
      released = true;
      release();
    }
  };

  const stop = () => {
    if (stopped) return;
    stopped = true;
    detachListeners();
    // Tell the API to terminate the exec stream for this instance.
    try {
      socket.emit(INSTANCE_EXEC_STOP, { instanceId: payload.instanceId });
    } catch {
      // ignore — the disconnect path also cleans up server-side.
    }
  };

  socket.on(INSTANCE_EXEC_OUTPUT, outputHandler);
  socket.on(INSTANCE_EXEC_READY, readyHandler);
  socket.on(INSTANCE_EXEC_END, endHandler);
  detachTeardown = wireSocketTeardown(socket, () => {
    // On a socket drop, tear down locally; do NOT emit (the socket is gone).
    if (stopped) return;
    stopped = true;
    detachListeners();
    onEnd?.();
  });

  // Start the session — argv under `cmd`, scoped by instanceId.
  socket.emit(INSTANCE_EXEC_START, {
    instanceId: payload.instanceId,
    cmd: payload.cmd,
  });

  return {
    send: (input: string) => {
      if (stopped) return;
      try {
        // The API reads `payload.data` and forwards the bytes to the PTY.
        socket.emit(INSTANCE_EXEC_INPUT, {
          instanceId: payload.instanceId,
          data: input,
        });
      } catch {
        // ignore transient emit failures
      }
    },
    resize: (cols: number, rows: number) => {
      if (stopped) return;
      try {
        // Best-effort PTY resize relay; the API validates/clamps and resolves
        // the Docker execId from its own session map (never from us).
        socket.emit(INSTANCE_EXEC_RESIZE, {
          instanceId: payload.instanceId,
          cols,
          rows,
        });
      } catch {
        // ignore — a missed resize just leaves the PTY at its previous size.
      }
    },
    stop,
  };
}

/**
 * Subscribe to the periodic fleet-status fan-out the API's reconcile sweep
 * publishes (`"client-instances instance-status"` — a Feathers custom service
 * event on the staff channel). The grid patches itself in place from the
 * envelope, so a container crash / vanished container shows without a manual
 * refresh.
 *
 * Unlike the per-container streams this IS a translated fan-out, so it goes
 * through the bus's `instanceStatusChannel` (envelopes without an `instances`
 * array are dropped there). Returns an idempotent disposer.
 *
 * The return type keeps its historical `| null` so the caller's
 * "unbound — retry next time" branch still type-checks, but the bus always
 * hands back a real disposer and binds the listener as soon as a connection
 * resolves; the caller no longer has to retry.
 */
export async function subscribeInstanceStatus(
  rootStore: RootStore,
  onStatus: (envelope: InstanceStatusEnvelope) => void
): Promise<(() => void) | null> {
  return subscribeRealtime(rootStore, instanceStatusChannel, onStatus);
}

/**
 * Subscribe to the registry sweep's update-availability fan-out
 * (`"client-instances instance-update-available"`). Emitted ONLY when an
 * instance flips to update-available, so handlers mark badges on — clearing
 * happens via the status feed / reloads. Same contract as
 * {@link subscribeInstanceStatus}: idempotent disposer, binds when a
 * connection resolves.
 */
export async function subscribeInstanceUpdates(
  rootStore: RootStore,
  onUpdate: (envelope: InstanceUpdateEnvelope) => void
): Promise<(() => void) | null> {
  return subscribeRealtime(rootStore, instanceUpdateChannel, onUpdate);
}

/**
 * Subscribe to the per-step progress of container actions
 * (`"client-instances instance-action-progress"`). Every action by anyone on
 * the staff channel arrives; the caller filters on its own instance id and
 * run (see InstanceActionsStore.watchProgress). Same contract as
 * {@link subscribeInstanceStatus}.
 */
export async function subscribeInstanceActionProgress(
  rootStore: RootStore,
  onProgress: (event: InstanceActionProgressEvent) => void
): Promise<(() => void) | null> {
  return subscribeRealtime(rootStore, instanceActionProgressChannel, onProgress);
}
